import { useMemo, memo } from "react"
import { cn } from "@/lib/utils"
import { useAuth } from "@/contexts/auth"
import { NavLink, useLocation } from "react-router-dom"
import { UserRole } from "@/types/enums"
import { 
  HomeIcon, 
  CheckSquareIcon, 
  PieChartIcon, 
  UsersIcon, 
  SettingsIcon,
  FileTextIcon,
  ShieldCheck,
  Crown,
  Building,
  ChevronRight,
  Calendar
} from "lucide-react"
import ShiftSageLogo from '@/images/Shift-Sage.png'

// Memoized sidebar link component for better performance
const SidebarLink = memo(({ 
  link, 
  isActive, 
  collapsed
}: {
  link: { path: string; icon: JSX.Element; label: string };
  isActive: boolean;
  collapsed: boolean;
}) => {
  const linkClass = cn(
    "flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-150",
    isActive 
      ? "bg-primary/10 text-primary shadow-sm" 
      : "text-muted-foreground hover:text-foreground hover:bg-accent",
    collapsed && "justify-center px-2"
  );

  return (
    <NavLink
      to={link.path}
      className={linkClass}
      title={collapsed ? link.label : undefined}
    >
      {link.icon}
      {!collapsed && (
        <span>
          {link.label}
        </span>
      )}
      {!collapsed && isActive && (
        <div 
          className="ml-auto"
        >
          <ChevronRight className="h-4 w-4" />
        </div>
      )}
    </NavLink>
  );
});

SidebarLink.displayName = "SidebarLink";

// Memoized sidebar section component
const SidebarSection = memo(({ 
  title, 
  links, 
  collapsed, 
  isActive
}: {
  title?: string;
  links: Array<{ path: string; icon: JSX.Element; label: string }>;
  collapsed: boolean;
  isActive: (path: string) => boolean;
}) => (
  <div className={cn("py-2", collapsed && "px-2")}>
    {!collapsed && title && (
      <h2 className="mb-1 text-[13px] font-semibold tracking-widest text-gray-500 uppercase border-l-2 border-gray-300/60 pl-4 bg-transparent">
        {title}
      </h2>
    )}
    <div className="space-y-1 px-1 mt-5">
      {links.map((link) => (
        <SidebarLink
          key={link.path}
          link={link}
          isActive={isActive(link.path)}
          collapsed={collapsed}
        />
      ))}
    </div>
  </div>
));

SidebarSection.displayName = "SidebarSection";

export function Sidebar({ className }: React.HTMLAttributes<HTMLDivElement>) {
  const { user } = useAuth()
  const location = useLocation()

  // Memoized menu links for better performance
  const menuSections = useMemo(() => {
    // Common links for all users
    const commonLinks = [
      { path: "/", icon: <HomeIcon className="h-4 w-4" />, label: "Home" },
      { path: "/audit", icon: <PieChartIcon className="h-4 w-4" />, label: "Audit" }
      ];
    
    // Department Head specific links
      const departmentHeadLinks: Array<{ path: string; icon: JSX.Element; label: string }> = [
        { path: "/pending-requests", icon: <CheckSquareIcon className="h-4 w-4" />, label: "Pending Requests" },
        { path: "/department-employees", icon: <UsersIcon className="h-4 w-4" />, label: "Employees" }
      ];
      


    // Plant Manager links
    const plantManagerLinks = [
      { path: "/pending-requests", icon: <CheckSquareIcon className="h-4 w-4" />, label: "Pending Requests" }
      ];
    
    // HR links
    const hrLinks = [
      { path: "/pending-requests", icon: <CheckSquareIcon className="h-4 w-4" />, label: "Pending Requests" },
      { path: "/employees", icon: <UsersIcon className="h-4 w-4" />, label: "Management" },
      { path: "/duty-roster", icon: <Calendar className="h-4 w-4" />, label: "Duty Roster" },
      { path: "/reports", icon: <FileTextIcon className="h-4 w-4" />, label: "Reports" },
      { path: "/settings", icon: <SettingsIcon className="h-4 w-4" />, label: "Company Settings" },
      ];

    // Super Admin links
    const superAdminLinks = [
      { path: "/pending-requests", icon: <ShieldCheck className="h-4 w-4" />, label: "Approvals" },
      { path: "/employees", icon: <UsersIcon className="h-4 w-4" />, label: "Management" },
      { path: "/duty-roster", icon: <Calendar className="h-4 w-4" />, label: "Duty Roster" },
      { path: "/settings", icon: <SettingsIcon className="h-4 w-4" />, label: "Settings" },
      ];
    
    // App Owner links - Removed Configure Users
    const appOwnerLinks = [
      { path: "/companies", icon: <Building className="h-4 w-4" />, label: "Companies" },
      { path: "/impersonate", icon: <Crown className="h-4 w-4" />, label: "Impersonate Users" },
      { path: "/settings", icon: <SettingsIcon className="h-4 w-4" />, label: "Settings" },
      ];

      const sections = [
        { title: undefined, links: commonLinks }
      ];

      if (user?.role === UserRole.DEPARTMENT_HEAD && departmentHeadLinks.length > 0) {
        sections.push({ title: "Department", links: departmentHeadLinks });
      }
      if (user?.role === UserRole.PLANT_MANAGER) {
        sections.push({ title: "Management", links: plantManagerLinks });
      }
      if (user?.role === UserRole.HR) {
        sections.push({ title: "HR Tools", links: hrLinks });
      }
      if (user?.role === UserRole.SUPER_ADMIN) {
        sections.push({ title: "Administration", links: superAdminLinks });
      }
      if (user?.role === UserRole.APP_OWNER) {
        sections.push({ title: "App Owner", links: appOwnerLinks });
      }

      return sections;
    }, [user?.role]);

  if (!user) return null;

  const isActive = (path: string) => {
    return location.pathname === path
  }

  return (
    <aside
      className={cn(
        "py-0 h-full flex flex-col bg-background/80 border-r backdrop-blur-sm",
        className
      )}
      style={{ width: 256 }}
    >
      <div className="h-16 flex items-center border-b px-4">
        <div className="flex items-center w-full justify-center text-center">
          <NavLink to="/" className="focus:outline-none" aria-label="Go to home page">
            <div className="flex items-center justify-center gap-2">
              <img 
                src={ShiftSageLogo} 
                alt="ShiftSage Logo" 
                className="transition-all duration-150 h-10 w-10"
              />
              <span className="font-bold text-2xl tracking-tight">
                ShiftSage
              </span>
            </div>
          </NavLink>
        </div>
      </div>
      <div className="flex-1">
        <div className="space-y-6">
          <SidebarSection
            title="Main Menu"
            links={menuSections[0].links}
            collapsed={false}
            isActive={isActive}
          />
          {menuSections.slice(1).map((section, index) => (
            <SidebarSection
              key={index}
              title={section.title}
              links={section.links}
              collapsed={false}
              isActive={isActive}
            />
          ))}
        </div>
      </div>
    </aside>
  )
}

export default Sidebar
